import { useGlobalData } from "@/app/global_data"
import { join } from "@/app/utils"
import { useSidebarNavigation } from "@/components/sidebar_menu/hooks/use_sidebar_menu"
import { MenuItem } from "@/components/sidebar_menu/sidebar_menu.options_definition"
import { CaretDown, CaretRight } from "@phosphor-icons/react"
import { usePathname } from "next/navigation"
import { useEffect, useMemo, useState } from "react"

type SidebarMenuItem = {
  item: MenuItem
  isMobile?: boolean
  onAction?: () => void
}

// common component for both mobile and desktop sidebar
export function SidebarMenuItem({
  item,
  onAction,
  isMobile = false,
}: SidebarMenuItem) {
  const globalData = useGlobalData()
  const { filter, entity, chart } = globalData.get()
  const pathname = usePathname()

  const [lastActiveChartGroup, setLastActiveChartGroup] = useState("")

  const { getActiveItems, isChartsPage, handleNavigationClick } =
    useSidebarNavigation(globalData)

  const { activeItem, parentItem } = useMemo(() => {
    return getActiveItems(pathname!)
  }, [filter, entity, chart, pathname])

  const isParentActive = parentItem?.id === item.id
  const isItemActive = activeItem?.id === item.id

  const hasChildren = item.children && item.children.length > 0

  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (isParentActive || isItemActive) {
      setIsOpen(true)
    }
  }, [isParentActive, isItemActive])

  useEffect(() => {
    const isMobileScreen = window && window.innerWidth < 600
    if (isMobileScreen && !isParentActive && !isItemActive) {
      setIsOpen(false)
    }
  }, [isParentActive, isItemActive])

  useEffect(() => {
    if (globalData.isLoaded() && globalData.getChart().groupName()) {
      setLastActiveChartGroup(globalData.getChart().groupName())
    }
  }, [globalData.isLoaded(), chart])

  return (
    <li>
      <div className="flex w-full items-center justify-between px-2 py-1 font-semibold text-main-700 focus:shadow-none">
        <div
          onClick={() => {
            if (item.url) handleNavigationClick(item.url)
            if (onAction) onAction()
          }}
          className={`flex cursor-pointer select-none items-center gap-2 transition-colors duration-300 ease-in-out hover:text-primary-500 sm:text-xs ${isItemActive || isParentActive ? "text-primary-500" : ""}`}>
          {item.icon && <item.icon className="size-5 shrink-0" />}
          {item.name}
        </div>

        {hasChildren && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none">
            {isMobile ?
              <CaretRight
                className={`h-6 w-6 transform transition ${isOpen ? "rotate-90" : ""}`}
              />
            : <CaretDown
                className={`size-4 shrink-0 transform transition ${isOpen ? "-rotate-90" : ""}`}
              />
            }
          </button>
        )}
      </div>

      {hasChildren && isOpen && (
        <ul className="flex transform flex-col space-y-1 px-2">
          {item.children?.map((childItem) => {
            const currentChartCode =
              globalData.isLoaded() ?
                globalData.getChart().groupName
              : lastActiveChartGroup

            const isChildActiveChartOption =
              isChartsPage && currentChartCode === childItem.name

            const isChildActive = activeItem?.id === childItem.id

            return (
              <li key={childItem.id || childItem.name}>
                <div
                  onClick={() => {
                    handleNavigationClick(childItem.url, childItem.chartCode)
                    if (onAction) onAction()
                  }}
                  className={join(
                    "block cursor-pointer rounded-md py-1 pl-9 pr-2 text-sm text-main-700 sm:text-xs",
                    isChildActiveChartOption || isChildActive ?
                      "bg-main-100 text-primary-500"
                    : "hover:bg-main-100 hover:text-primary-500"
                  )}>
                  {childItem.name}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}
